import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {from, of, Subject, Subscription, timer} from 'rxjs';
import {debounce, switchMap} from 'rxjs/operators';
import {CmsApiService} from '../../../../core/cms-api.service';
import {LoggerService} from '../../../../core/logger.service';
import {ConceptOption, conceptOptionAncestors, conceptOptionLabel} from '../../concept-picker/concept-option.model';

/**
 * Search-as-you-type picker over one concept target field's whole list,
 * through `POST /concept_terms` with `q` - the list can hold far more
 * terms than a preloaded option list would carry. Emits the chosen term
 * and clears itself, so a caller applies the pick (to one review row, or
 * to every selected one) and the field is ready for the next search.
 */
@Component({
  selector: 'app-concept-term-search',
  templateUrl: './concept-term-search.component.html',
  styleUrls: ['./concept-term-search.component.scss'],
  standalone: false,
})
export class ConceptTermSearchComponent implements OnInit, OnDestroy {
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  @Input({required: true}) fieldId!: string;
  @Input({required: true}) objectType!: string;
  /** TRANS key for the field label. */
  @Input() label = 'TRANS__IMPORT__VALUE_REVIEW__SEARCH_TERM';

  @Output() termPicked = new EventEmitter<ConceptOption>();

  readonly inputControl = new FormControl<string | ConceptOption>('', {nonNullable: true});
  options: ConceptOption[] = [];
  searching = false;
  /** The last query a search finished for; the "no hits" line shows only
   * once one has. */
  searchedTerm = '';

  optionLabel = conceptOptionLabel;
  optionAncestors = conceptOptionAncestors;

  private readonly queries = new Subject<string>();
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      // An empty term (a cleared input) skips the debounce and cancels a
      // search in flight, so its late answer cannot refill the options.
      this.queries.pipe(
        debounce(term => term ? timer(250) : of(0)),
        switchMap(term => term
          ? from(this.search(term).then(options => ({term, options})))
          : of({term, options: [] as ConceptOption[]})),
      ).subscribe(({term, options}) => {
        this.options = options;
        this.searchedTerm = term;
        this.searching = false;
      }));

    this.subscriptions.add(this.inputControl.valueChanges.subscribe(value => {
      if (typeof value !== 'string') {
        return;
      }
      const term = value.trim();
      if (term) {
        this.searching = true;
        this.queries.next(term);
      } else {
        this.queries.next('');
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.queries.complete();
  }

  displayFn = (value: string | ConceptOption | null): string =>
    typeof value === 'string' ? value : conceptOptionLabel(value ?? undefined);

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.termPicked.emit(event.option.value as ConceptOption);
    this.options = [];
    this.searchedTerm = '';
    this.inputControl.setValue('', {emitEvent: false});
    this.queries.next('');
  }

  private async search(term: string): Promise<ConceptOption[]> {
    try {
      const res = await this.cms.importModuleConceptTerms({
        field_id: this.fieldId, object_type: this.objectType, q: term});
      return res.terms;
    } catch (e) {
      this.logger.error(`concept term search: search failed for "${term}": ${e}`);
      return [];
    }
  }
}
